<template>
  <div class="max-w-6xl mx-auto px-4 py-12">

    <div class="max-w-lg mx-auto">
      <!-- Loading skeleton -->
      <div v-if="loading" class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
        <div class="h-16 w-16 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center mb-6 animate-pulse">
          <svg class="h-8 w-8 text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        </div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Verifying your payment...</h2>
        <p class="mt-2 text-gray-500 dark:text-gray-400">Please wait while we confirm your subscription.</p>
      </div>

      <!-- Subscription found -->
      <div v-else-if="subscription">
        <!-- ACTIVE — success -->
        <div v-if="subscription.status === 'ACTIVE'" class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border-2 border-green-400/50 dark:border-green-500/30 shadow-[0_0_40px_-10px_rgba(74,222,128,0.2)]">
          <div class="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mb-6">
            <svg class="h-10 w-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 class="text-3xl font-bold text-gray-900 dark:text-white">Payment Successful!</h2>
          <p class="mt-4 text-lg text-gray-600 dark:text-gray-400 text-center">
            Your <strong class="text-gray-900 dark:text-white">{{ subscription.flavor }}</strong> plan is now fully active. Thank you for subscribing!
          </p>
          <router-link
            to="/"
            class="mt-8 inline-flex items-center rounded-xl bg-indigo-600 px-8 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Get Started
          </router-link>
        </div>

        <!-- PENDING — processing -->
        <div v-else-if="subscription.status === 'PENDING'" class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
          <div class="h-16 w-16 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center mb-6">
            <svg class="h-8 w-8 text-indigo-500 dark:text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Processing your subscription</h2>
          <p class="mt-3 text-gray-600 dark:text-gray-400 text-center">
            We've received your payment and are waiting for confirmation. This usually takes a few moments.
          </p>
          <div class="mt-4 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
            <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            <span>Refreshing in {{ timer }}s</span>
          </div>
          <router-link
            to="/dashboard"
            class="mt-6 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
          >
            Continue to Dashboard anyway →
          </router-link>
        </div>

        <!-- ON_HOLD — payment issue -->
        <div v-else-if="subscription.status === 'ON_HOLD'" class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-amber-300 dark:border-amber-700/50 shadow-[0_0_30px_-10px_rgba(251,191,36,0.15)]">
          <div class="h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mb-6">
            <svg class="h-8 w-8 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Action Required</h2>
          <p class="mt-3 text-gray-600 dark:text-gray-400 text-center">
            Your subscription is currently on hold. We were unable to process your recent payment and your features have been temporarily paused.
          </p>
          <router-link
            to="/profile"
            class="mt-8 inline-flex items-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Update Payment Method
          </router-link>
        </div>

        <!-- CANCELLED / EXPIRED — terminal -->
        <div v-else-if="subscription.status === 'CANCELLED' || subscription.status === 'EXPIRED'" class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/30 shadow-sm">
          <div class="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-6">
            <svg class="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
            {{ subscription.status === 'CANCELLED' ? 'Checkout Failed' : 'Subscription Expired' }}
          </h2>
          <p class="mt-3 text-gray-600 dark:text-gray-400 text-center">
            {{ subscription.status === 'CANCELLED'
              ? 'Your checkout process was aborted or the payment failed. No transactions were executed.'
              : 'Your previous subscription has ended. Choose a plan to safely reactivate your account.'
            }}
          </p>
          <router-link
            to="/billing"
            class="mt-8 inline-flex items-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            {{ subscription.status === 'CANCELLED' ? 'Try Again' : 'View Plans' }}
          </router-link>
        </div>

        <!-- Unknown status -->
        <div v-else class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 shadow-sm">
          <div class="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mb-6">
            <svg class="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Something went wrong</h2>
          <p class="mt-3 text-gray-600 dark:text-gray-400 text-center">
            Your subscription status is <strong class="text-gray-900 dark:text-white">{{ formatEnum(subscription.status) }}</strong>. If you believe this is an error, please contact support.
          </p>
          <router-link
            to="/billing"
            class="mt-8 inline-flex items-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Back to Pricing
          </router-link>
        </div>
      </div>

      <!-- No subscription -->
      <div v-else class="flex flex-col items-center p-10 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm">
        <div class="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
          <svg class="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">No subscription found</h2>
        <p class="mt-3 text-gray-600 dark:text-gray-400 text-center">We couldn't find a subscription for your account.</p>
        <p v-if="subscriptionStore.error" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ subscriptionStore.error }}</p>
        <router-link
          to="/billing"
          class="mt-8 inline-flex items-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          View Plans
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useSubscriptionStore } from '@/stores/subscription';
import { formatEnum } from '@/lib/format';

const subscriptionStore = useSubscriptionStore();
const loading = computed(() => subscriptionStore.loading);
const subscription = computed(() => subscriptionStore.subscription);
const timer = ref(5);
let interval: ReturnType<typeof setInterval> | null = null;

async function checkStatus() {
  await subscriptionStore.fetchSubscription();
}

onMounted(() => {
  checkStatus();
});

onUnmounted(() => {
  if (interval) clearInterval(interval);
});

watch(subscription, (newSub) => {
  if (newSub?.status === 'PENDING' && !interval) {
    interval = setInterval(() => {
      timer.value--;
      if (timer.value <= 0) {
        timer.value = 5;
        checkStatus();
      }
    }, 1000);
  } else if (newSub?.status !== 'PENDING' && interval) {
    clearInterval(interval);
    interval = null;
  }
}, { immediate: true });
</script>
